import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CreateInstitutionStaffDto,
  InstitutionStaffDto,
  UpdateInstitutionStaffDto,
} from "@niutraan/shared-dto";
import { apiClient } from "@niutraan/hooks/src/lib/api";

// Query keys
const QUERY_KEYS = {
  institutionStaff: "institutionStaff",
  activeInstitutionStaff: "activeInstitutionStaff",
};

// Get all institution staff
export const useInstitutionStaff = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.institutionStaff],
    queryFn: async () => {
      const data =
        await apiClient.get<InstitutionStaffDto[]>("/institution-staff");
      return data;
    },
  });
};

// Get active institution staff
export const useActiveInstitutionStaff = () => {
  return useQuery({
    queryKey: [QUERY_KEYS.activeInstitutionStaff],
    queryFn: async () => {
      const data = await apiClient.get<InstitutionStaffDto[]>(
        "/institution-staff/active",
      );
      return data;
    },
  });
};

// Get institution staff by ID
export const useInstitutionStaffById = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.institutionStaff, id],
    queryFn: () =>
      apiClient.get<InstitutionStaffDto>(`/institution-staff/${id}`),
    enabled: !!id,
  });
};

// Create institution staff
export const useCreateInstitutionStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateInstitutionStaffDto) =>
      apiClient.post<InstitutionStaffDto>("/institution-staff", data),
    onSuccess: () => {
      // Invalidate queries to refetch data
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.institutionStaff],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.activeInstitutionStaff],
      });
    },
  });
};

// Update institution staff
export const useUpdateInstitutionStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateInstitutionStaffDto;
    }) => apiClient.put<InstitutionStaffDto>(`/institution-staff/${id}`, data),
    onSuccess: (data) => {
      // Update cache directly
      queryClient.setQueryData([QUERY_KEYS.institutionStaff, data.id], data);
      // Invalidate lists to refetch
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.institutionStaff],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.activeInstitutionStaff],
      });
    },
  });
};

// Delete institution staff
export const useDeleteInstitutionStaff = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) =>
      apiClient.delete<InstitutionStaffDto>(`/institution-staff/${id}`),
    onSuccess: (_, id) => {
      // Remove from cache
      queryClient.removeQueries({
        queryKey: [QUERY_KEYS.institutionStaff, id],
      });
      // Invalidate lists to refetch
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.institutionStaff],
      });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.activeInstitutionStaff],
      });
    },
  });
};
